// -----------------------------------------------------------------------------
// Full-screen lightbox for chat image attachments. Shows the full-size
// image (already fetched as an object URL by the caller) with a download
// action, click-to-zoom, and Escape / backdrop-click to close. Rendered
// in place of opening the blob in a new browser tab so viewing images
// keeps the user inside the app.
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";

interface ChatImageLightboxProps {
  /** Object URL of the full-size image. The caller owns revocation. */
  url: string;
  fileName: string;
  onDownload: () => void;
  onClose: () => void;
}

export default function ChatImageLightbox({
  url,
  fileName,
  onDownload,
  onClose,
}: ChatImageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/85"
      onClick={onClose}
    >
      <div
        className="flex items-center justify-between gap-2 px-4 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="min-w-0 truncate text-sm font-medium text-white/90">
          {fileName}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setZoomed((z) => !z)}
            title={zoomed ? "Zoom out" : "Zoom in"}
            className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
          >
            {zoomed ? (
              <MagnifyingGlassMinusIcon className="h-5 w-5" />
            ) : (
              <MagnifyingGlassPlusIcon className="h-5 w-5" />
            )}
          </button>
          <button
            type="button"
            onClick={onDownload}
            title={`Download ${fileName}`}
            className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        className={classNames(
          "flex min-h-0 flex-1 items-center justify-center overflow-auto p-4",
          zoomed ? "cursor-zoom-out" : "cursor-zoom-in"
        )}
      >
        <img
          src={url}
          alt={fileName}
          onClick={(e) => {
            e.stopPropagation();
            setZoomed((z) => !z);
          }}
          className={classNames(
            "select-none",
            zoomed
              ? "max-h-none max-w-none"
              : "max-h-full max-w-full object-contain"
          )}
        />
      </div>
    </div>
  );
}
